import React, { FunctionComponent } from 'react';
import { Box, Fade, IconButton, Stack, Typography } from '@mui/material';
import {
  ArrowBackIosRounded as ArrowBackIosRoundedIcon,
  AddCircle as AddCircleIcon,
} from '@mui/icons-material';

import {
  getConversationAvatar,
  getConversationName,
  getDetailedLastSeen,
} from '../../chatHelpers';
import {
  ADMIN_CHAT_VIEW_MODES,
  TAdminChatViewMode,
} from '../../../../../store/V2/AdminChatSlice';
import { TP_COLORS } from '../../../../../constants';
import { useAppSelector } from '../../../../../hooks';
import { getAuthState } from '../../../../../store/AuthSlice';
import { IConversation } from '../../../../../store/V2/ChatSlice';
import { getCourseStudentsState } from '../../../../../store/V2/CourseStudentsSlice';
import { getCourseTutorsState } from '../../../../../store/V2/CourseTutorsSlice';

interface AdminChatHeaderProps {
  viewMode: TAdminChatViewMode;
  toggleChatCollapse: () => void;
  handleBack: () => void;
  selectedConversation?: IConversation;
  onAddNewConversation: () => void;
}

const AdminChatHeader: FunctionComponent<AdminChatHeaderProps> = ({
  viewMode,
  toggleChatCollapse,
  handleBack,
  selectedConversation,
  onAddNewConversation,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);
  const courseStudentsState = useAppSelector(getCourseStudentsState);
  const courseTutorsState = useAppSelector(getCourseTutorsState);

  const authUser = authState.user;

  const CHAT_COLLAPSE_ICON_BUTTON = (
    <IconButton
      onClick={toggleChatCollapse}
      sx={{
        backgroundColor: '#F8FAFC',
        borderRadius: '5px',
        marginLeft: '8px',
      }}
    >
      <img
        style={{ width: '22px', height: '18px' }}
        src={`${process.env.PUBLIC_URL}/images/chat/chat-collapse.png`}
        alt="chat-collapse"
      />
    </IconButton>
  );

  const renderContents = () => {
    if (viewMode === ADMIN_CHAT_VIEW_MODES.NEW_CONVERSATION) {
      return (
        <>
          <Box display="flex" alignItems="center">
            <IconButton size="small" onClick={handleBack}>
              <ArrowBackIosRoundedIcon />
            </IconButton>
            <Typography
              variant="h5"
              fontWeight="600"
              marginLeft="7px"
              color={TP_COLORS.gray.dark}
            >
              New Chat
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            {CHAT_COLLAPSE_ICON_BUTTON}
          </Box>
        </>
      );
    }

    if (viewMode === ADMIN_CHAT_VIEW_MODES.CONVERSATION) {
      return (
        <>
          <Box display="flex" alignItems="center">
            <IconButton size="small" onClick={handleBack}>
              <ArrowBackIosRoundedIcon />
            </IconButton>
            {selectedConversation ? (
              <>
                {getConversationAvatar(selectedConversation, authUser?._id)}
                <Box marginLeft="10px">
                  <Typography
                    fontSize="16px"
                    fontWeight="600"
                    lineHeight="20px"
                    color={TP_COLORS.gray.dark}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {getConversationName(selectedConversation, authUser?._id)}
                  </Typography>
                  {selectedConversation.users.length > 1 ? (
                    <Typography fontSize="12px" color="#647488">
                      <Fade in>
                        <span>
                          {getDetailedLastSeen(
                            selectedConversation,
                            authUser?._id || ''
                          )}
                        </span>
                      </Fade>
                    </Typography>
                  ) : null}
                </Box>
              </>
            ) : null}
          </Box>
          <Box display="flex" alignItems="center">
            {CHAT_COLLAPSE_ICON_BUTTON}
          </Box>
        </>
      );
    }

    return (
      <>
        <Box display="flex" alignItems="center">
          <Typography
            variant="h5"
            fontWeight="600"
            marginLeft="7px"
            color={TP_COLORS.gray.dark}
          >
            Chats
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={onAddNewConversation}
            disabled={
              courseStudentsState.status === 'loading' ||
              courseTutorsState.status === 'loading'
            }
          >
            <AddCircleIcon sx={{ color: '#0D71D9' }} />
          </IconButton>
          {CHAT_COLLAPSE_ICON_BUTTON}
        </Box>
      </>
    );
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      padding="18px 13px 8px"
    >
      {renderContents()}
    </Stack>
  );
};

export default AdminChatHeader;
